// Animation authoring for the cutout rig. Every action is a list of key poses
// interpolated with easing (idle and run are periodic and generated from
// smooth loops / a gait model instead). Poses are expressed relative to the
// mech's own proportions so the three mechs share choreography while keeping
// their persona (VOLT leans in and lunges, BASTION plants and stomps, WRAITH
// floats and folds low).
//
// Keys use anchor-relative values:
//   hip  = [dx, drop]: pelvis x offset from the anchor, and how far it sits BELOW the neutral height
//   legs = { x: ankle x offset from the anchor, lift: sole height above the ground (0 = planted), a: foot pitch }
//   head = absolute rotation (stable gaze), torso = lean (positive = forward)
//   arms = absolute [upper, fore] angles for the arm pose metadata (right-facing)

import { FOOT_ANCHOR } from '../../config/constants';
import { hashSeed } from '../../core/rng';
import type { AnimKey, ArmPose, MechId } from '../../core/types';
import { ankleYForLift, type RigPose } from './rig';
import type { MechStyle, Pt, RunGait } from './styles';

/** Painter parameters that vary per frame. */
export interface FrameFx {
  anim: AnimKey;
  index: number;
  /** 0..1 progress through the action. */
  t: number;
  /** Emissive intensity (1 = nominal; idle pulses, death fades to 0). */
  glow: number;
  /** 0..1 signature flare: floating parts spread, strips overcharge, vents open. */
  flare: number;
  /** Floating-part bob offset in px (WRAITH). */
  bob: number;
  /** 0..1 shutdown sparks (death). */
  sparks: number;
  /** 0..1 static discharge arcs around the frame (VOLT signature). */
  arcs: number;
  /** 0..1 heel anchors deployed (BASTION signature). */
  anchor: number;
  /** 0..1 floating parts powered down and docked onto the frame (WRAITH death). */
  dock: number;
  /** Deterministic per-frame seed for sparks/arcs. */
  seed: number;
}

export interface FrameSpec {
  pose: RigPose;
  fx: FrameFx;
  armF: ArmPose;
  armR: ArmPose;
  aimWeight: number;
  recoil?: number;
  lift?: number;
  /** At least one foot rests on the ground line (validated against the solved skeleton). */
  grounded: boolean;
}

/** Suggested playback per action. */
export const ANIM_TIMING: Readonly<Record<AnimKey, { fps: number; loop: boolean }>> = {
  idle: { fps: 8, loop: true },
  run: { fps: 18, loop: true },
  jump: { fps: 12, loop: false },
  dash: { fps: 24, loop: false },
  shoot: { fps: 30, loop: false },
  signature: { fps: 20, loop: false },
  hurt: { fps: 16, loop: false },
  death: { fps: 14, loop: false },
  land: { fps: 20, loop: false },
};

// ───────────────────────────── Keyframe model ─────────────────────────────

type EaseName = 'io' | 'o' | 'i' | 'l';
const EASE: Record<EaseName, (u: number) => number> = {
  io: (u) => u * u * (3 - 2 * u),
  o: (u) => 1 - (1 - u) * (1 - u) * (1 - u),
  i: (u) => u * u * u,
  l: (u) => u,
};

interface LegKey {
  x: number;
  lift?: number;
  a?: number;
}

interface Key {
  f: number;
  hip: Pt;
  torso: number;
  head: number;
  pelvis?: number;
  F: LegKey;
  R: LegKey;
  armF: Pt;
  armR: Pt;
  glow?: number;
  flare?: number;
  sparks?: number;
  arcs?: number;
  anchor?: number;
  dock?: number;
  bob?: number;
  /** Easing used from the previous key into this one. */
  ease?: EaseName;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
const smooth = (u: number): number => EASE.io(clamp01(u));

/** Arms holding the weapon at the ready (used for blending when aimWeight < 1). */
const CARRY_F: Pt = [1.12, 0.28];
const CARRY_R: Pt = [0.95, 0.18];

function toPose(style: MechStyle, hip: Pt, torso: number, head: number, pelvis: number, F: Required<LegKey>, R: Required<LegKey>): RigPose {
  return {
    hx: FOOT_ANCHOR.x + hip[0],
    hy: FOOT_ANCHOR.y - style.standHip + hip[1],
    pelvis,
    torso,
    head,
    legF: { x: FOOT_ANCHOR.x + F.x, y: ankleYForLift(style, F.a, F.lift), a: F.a },
    legR: { x: FOOT_ANCHOR.x + R.x, y: ankleYForLift(style, R.a, R.lift), a: R.a },
  };
}

function fxDefaults(style: MechStyle, anim: AnimKey, index: number, count: number): FrameFx {
  return {
    anim,
    index,
    t: count > 1 ? index / (count - 1) : 0,
    glow: 1,
    flare: 0,
    bob: style.floatBob * 0.6 * Math.sin((index / count) * Math.PI * 2),
    sparks: 0,
    arcs: 0,
    anchor: 0,
    dock: 0,
    seed: hashSeed(style.id, anim, index),
  };
}

function sampleKeys(
  style: MechStyle,
  anim: AnimKey,
  keys: readonly Key[],
  count: number,
  aimWeight: (i: number) => number,
): FrameSpec[] {
  const out: FrameSpec[] = [];
  for (let i = 0; i < count; i++) {
    let hi = keys.findIndex((k) => k.f >= i);
    if (hi < 0) hi = keys.length - 1;
    const b = keys[hi];
    const a = keys[Math.max(0, hi - 1)];
    const span = b.f - a.f;
    const e = span > 0 ? EASE[b.ease ?? 'io'](clamp01((i - a.f) / span)) : 1;
    const n = (x: number | undefined, y: number | undefined, d: number): number => lerp(x ?? d, y ?? d, e);
    const leg = (p: LegKey, q: LegKey): Required<LegKey> => ({
      x: lerp(p.x, q.x, e),
      lift: n(p.lift, q.lift, 0),
      a: n(p.a, q.a, 0),
    });
    const F = leg(a.F, b.F);
    const R = leg(a.R, b.R);
    const fx = fxDefaults(style, anim, i, count);
    fx.glow = n(a.glow, b.glow, 1);
    fx.flare = n(a.flare, b.flare, 0);
    fx.sparks = n(a.sparks, b.sparks, 0);
    fx.arcs = n(a.arcs, b.arcs, 0);
    fx.anchor = n(a.anchor, b.anchor, 0);
    fx.dock = n(a.dock, b.dock, 0);
    if (a.bob !== undefined || b.bob !== undefined) fx.bob = n(a.bob, b.bob, fx.bob);
    out.push({
      pose: toPose(
        style,
        [lerp(a.hip[0], b.hip[0], e), lerp(a.hip[1], b.hip[1], e)],
        lerp(a.torso, b.torso, e),
        lerp(a.head, b.head, e),
        n(a.pelvis, b.pelvis, 0),
        F,
        R,
      ),
      fx,
      armF: { upper: lerp(a.armF[0], b.armF[0], e), fore: lerp(a.armF[1], b.armF[1], e) },
      armR: { upper: lerp(a.armR[0], b.armR[0], e), fore: lerp(a.armR[1], b.armR[1], e) },
      aimWeight: aimWeight(i),
      grounded: Math.min(F.lift, R.lift) < 0.05,
    });
  }
  return out;
}

// ───────────────────────────── Persona tuning ─────────────────────────────

interface Persona {
  /** Dash torso lean and trailing-leg reach (fraction of leg length). */
  dashLean: number;
  dashTrail: number;
  /** Jump apex tuck (fraction of leg length the feet lift). */
  tuck: number;
  /** Hurt flinch strength. */
  flinch: number;
  /** Death: kneel drop, prone drop, prone torso angle, leg reach when prone. */
  kneelDrop: number;
  kneelAnkle: number;
  proneDrop: number;
  proneHipX: number;
  proneLegX: number;
  proneLift: number;
}

const PERSONA: Record<MechId, Persona> = {
  volt: {
    dashLean: 0.72,
    dashTrail: 0.66,
    tuck: 0.48,
    flinch: 1,
    kneelDrop: 20,
    kneelAnkle: -7,
    proneDrop: 31,
    proneHipX: -1,
    proneLegX: -38,
    proneLift: 1,
  },
  bastion: {
    dashLean: 0.42,
    dashTrail: 0.5,
    tuck: 0.38,
    flinch: 0.7,
    kneelDrop: 17,
    kneelAnkle: -9,
    proneDrop: 22,
    proneHipX: -6,
    proneLegX: -33,
    proneLift: 1,
  },
  wraith: {
    dashLean: 0.66,
    dashTrail: 0.62,
    tuck: 0.5,
    flinch: 1.15,
    kneelDrop: 27,
    kneelAnkle: -9,
    proneDrop: 44,
    proneHipX: 4,
    proneLegX: -40,
    proneLift: 4,
  },
};

const legLen = (s: MechStyle): number => s.thigh + s.shin;
const midX = (s: MechStyle): number => (s.stanceF + s.stanceR) / 2;

// ───────────────────────────── Idle / shoot ─────────────────────────────

function idleFrames(s: MechStyle): FrameSpec[] {
  const out: FrameSpec[] = [];
  const count = 8;
  for (let i = 0; i < count; i++) {
    const ph = (i / count) * Math.PI * 2;
    const fx = fxDefaults(s, 'idle', i, count);
    fx.glow = 0.8 + 0.2 * Math.cos(ph);
    fx.bob = s.floatBob * Math.sin(ph);
    out.push({
      pose: toPose(
        s,
        [midX(s) + 0.4, s.idleBob * (0.5 - 0.5 * Math.cos(ph))],
        s.lean + s.idleSway * Math.sin(ph),
        -0.04 - s.idleSway * 0.6 * Math.sin(ph - 0.7),
        0.01 * Math.sin(ph),
        { x: s.stanceF, lift: 0, a: 0 },
        { x: s.stanceR, lift: 0, a: 0 },
      ),
      fx,
      armF: { upper: CARRY_F[0] + 0.03 * Math.sin(ph), fore: CARRY_F[1] },
      armR: { upper: CARRY_R[0] + 0.03 * Math.sin(ph), fore: CARRY_R[1] },
      aimWeight: 1,
      grounded: true,
    });
  }
  return out;
}

/** Shoot: idle stance with a small upper-body kick; the arms' recoil/lift live in the metadata. */
function shootFrames(s: MechStyle): FrameSpec[] {
  const kickTorso = [0, -0.035, -0.026, -0.014, -0.005, 0];
  const kickHip = [0, -0.9, -0.7, -0.35, -0.1, 0];
  const glow = [1, 1.3, 1.15, 1.06, 1.02, 1];
  return kickTorso.map((k, i) => {
    const fx = fxDefaults(s, 'shoot', i, 6);
    fx.glow = glow[i];
    fx.bob = 0;
    return {
      pose: toPose(
        s,
        [midX(s) + 0.4 + kickHip[i], 0],
        s.lean + k,
        -0.04 + k * 0.5,
        0,
        { x: s.stanceF, lift: 0, a: 0 },
        { x: s.stanceR, lift: 0, a: 0 },
      ),
      fx,
      armF: { upper: CARRY_F[0], fore: CARRY_F[1] },
      armR: { upper: CARRY_R[0], fore: CARRY_R[1] },
      aimWeight: 1,
      recoil: s.recoil[i],
      lift: s.lift[i],
      grounded: true,
    };
  });
}

// ───────────────────────────── Run (gait model) ─────────────────────────────

/** Uniform Catmull-Rom through points (with mirrored end tangents). */
function catmull(points: readonly (readonly number[])[], u: number): number[] {
  const n = points.length - 1;
  const s = clamp01(u) * n;
  const i = Math.min(n - 1, Math.floor(s));
  const t = s - i;
  const p = (k: number): readonly number[] => {
    if (k < 0) return points[0].map((v, j) => 2 * v - points[1][j]);
    if (k > n) return points[n].map((v, j) => 2 * v - points[n - 1][j]);
    return points[k];
  };
  const p0 = p(i - 1);
  const p1 = p(i);
  const p2 = p(i + 1);
  const p3 = p(i + 2);
  const t2 = t * t;
  const t3 = t2 * t;
  return p1.map(
    (_, j) =>
      0.5 *
      (2 * p1[j] + (-p0[j] + p2[j]) * t + (2 * p0[j] - 5 * p1[j] + 4 * p2[j] - p3[j]) * t2 + (-p0[j] + 3 * p1[j] - 3 * p2[j] + p3[j]) * t3),
  );
}

/** One leg of the run at cycle phase p: planted stance sweeping back, then a swing arc forward. */
function gaitLeg(g: RunGait, phase: number): Required<LegKey> {
  const p = ((phase % 1) + 1) % 1;
  if (p < g.duty) {
    const u = p / g.duty;
    const x = lerp(g.front, g.back, u);
    let a = 0;
    if (u < 0.22) a = g.contact * (1 - smooth(u / 0.22));
    else if (u > 0.66) a = g.toeOff * smooth((u - 0.66) / 0.34);
    return { x, lift: 0, a };
  }
  const u = (p - g.duty) / (1 - g.duty);
  const [x, lift, a] = catmull(
    [[g.back, 0, g.toeOff], g.kick, g.pass, g.drive, g.reach, [g.front, 0, g.contact]],
    u,
  );
  return { x, lift: Math.max(0.6, lift), a };
}

function runFrames(s: MechStyle): FrameSpec[] {
  const g = s.run;
  const count = 12;
  const out: FrameSpec[] = [];
  for (let i = 0; i < count; i++) {
    const p = i / count;
    const wave = Math.cos(4 * Math.PI * (p - g.lowPhase));
    const F = gaitLeg(g, p);
    const R = gaitLeg(g, p + 0.5);
    const torso = g.lean + g.leanOsc * Math.cos(4 * Math.PI * (p - g.lowPhase - 0.08));
    const fx = fxDefaults(s, 'run', i, count);
    fx.glow = 1;
    fx.bob = s.floatBob * 0.8 * Math.sin(4 * Math.PI * (p - g.lowPhase - 0.15));
    out.push({
      pose: toPose(
        s,
        [g.hipX, g.drop + g.bob * wave],
        torso,
        torso * (g.headFollow ?? 0.3) - 0.03 - 0.012 * wave,
        g.pelvisOsc * Math.sin(2 * Math.PI * p),
        F,
        R,
      ),
      fx,
      armF: { upper: CARRY_F[0] - 0.05 * Math.sin(2 * Math.PI * p), fore: CARRY_F[1] },
      armR: { upper: CARRY_R[0] + 0.05 * Math.sin(2 * Math.PI * p), fore: CARRY_R[1] },
      aimWeight: 1,
      grounded: Math.min(F.lift, R.lift) < 0.05,
    });
  }
  return out;
}

// ───────────────────────────── Jump / dash / hurt (shared choreography) ─────────────────────────────

function jumpKeys(s: MechStyle): Key[] {
  const L = legLen(s);
  const P = PERSONA[s.id];
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  return [
    // 0-1 takeoff: crouch, then explosive extension onto the toes
    { f: 0, hip: [c, 0.2 * L], torso: s.lean + 0.18, head: 0.02, F: { x: fF }, R: { x: fR }, armF: CARRY_F, armR: CARRY_R, glow: 1.1 },
    { f: 1, hip: [c + 1, -0.05 * L], torso: s.lean + 0.02, head: -0.06, F: { x: fF * 0.6, lift: 2, a: 0.62 }, R: { x: fR * 0.7, lift: 0.8, a: 0.85 }, armF: CARRY_F, armR: CARRY_R, glow: 1.25, ease: 'o' },
    // 2-3 rise: front knee leads, rear leg trails
    { f: 2, hip: [c + 1, -0.04 * L], torso: s.lean, head: -0.06, F: { x: fF + 2, lift: 0.3 * L, a: 0.48 }, R: { x: fR * 0.6, lift: 0.12 * L, a: 0.8 }, armF: CARRY_F, armR: CARRY_R, glow: 1.1 },
    { f: 3, hip: [c + 1, -0.04 * L], torso: s.lean + 0.02, head: -0.05, F: { x: fF + 3, lift: 0.4 * L, a: 0.38 }, R: { x: fR * 0.5, lift: 0.22 * L, a: 0.65 }, armF: CARRY_F, armR: CARRY_R },
    // 4-5 apex tuck
    { f: 4, hip: [c, -0.04 * L], torso: s.lean + 0.06, head: -0.03, F: { x: fF + 2, lift: P.tuck * L, a: 0.25 }, R: { x: -0.08 * L, lift: (P.tuck - 0.06) * L, a: 0.52 }, armF: CARRY_F, armR: CARRY_R },
    { f: 5, hip: [c, -0.04 * L], torso: s.lean + 0.05, head: -0.03, F: { x: fF + 2, lift: (P.tuck - 0.05) * L, a: 0.2 }, R: { x: -0.1 * L, lift: (P.tuck - 0.12) * L, a: 0.46 }, armF: CARRY_F, armR: CARRY_R },
    // 6-7 fall and brace for landing
    { f: 6, hip: [c, -0.035 * L], torso: s.lean - 0.02, head: -0.05, F: { x: fF + 2, lift: 0.24 * L, a: 0.14 }, R: { x: fR * 0.75, lift: 0.18 * L, a: 0.3 }, armF: CARRY_F, armR: CARRY_R },
    { f: 7, hip: [c, -0.02 * L], torso: s.lean - 0.01, head: -0.04, F: { x: fF + 1, lift: 0.1 * L, a: 0.05 }, R: { x: fR * 0.9, lift: 0.08 * L, a: 0.14 }, armF: CARRY_F, armR: CARRY_R },
  ];
}

function dashKeys(s: MechStyle): Key[] {
  const L = legLen(s);
  const P = PERSONA[s.id];
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  const lean = P.dashLean;
  const trail = P.dashTrail;
  // Arms: front arm tucks the weapon low and forward, rear arm sweeps back.
  const tuckF: Pt = [1.95, 0.45];
  const sweepR: Pt = [2.55, 2.85];
  return [
    // 0-1 anticipation crouch
    { f: 0, hip: [c - 1, 0.07 * L], torso: s.lean + 0.08, head: -0.02, F: { x: fF }, R: { x: fR }, armF: [1.5, 0.6], armR: [1.4, 0.9] },
    { f: 1, hip: [c - 2, 0.13 * L], torso: s.lean + 0.22, head: 0.04, F: { x: fF + 1 }, R: { x: fR - 1, a: 0.42 }, armF: [1.8, 0.7], armR: [2.0, 1.8], glow: 1.15 },
    // 2-5 thrust: strong forward lean, legs trailing
    { f: 2, hip: [c + 4, 0.02 * L], torso: lean - 0.06, head: 0.12, F: { x: -0.1 * L, lift: 0.18 * L, a: 0.6 }, R: { x: -(trail - 0.06) * L, lift: 0.28 * L, a: 1.1 }, armF: tuckF, armR: sweepR, glow: 1.5, flare: 0.5, ease: 'o' },
    { f: 3, hip: [c + 5, 0.02 * L], torso: lean, head: 0.16, F: { x: -0.16 * L, lift: 0.22 * L, a: 0.7 }, R: { x: -trail * L, lift: 0.33 * L, a: 1.2 }, armF: tuckF, armR: sweepR, glow: 1.6, flare: 0.6 },
    { f: 4, hip: [c + 5, 0.025 * L], torso: lean, head: 0.16, F: { x: -0.17 * L, lift: 0.23 * L, a: 0.72 }, R: { x: -trail * L, lift: 0.34 * L, a: 1.22 }, armF: tuckF, armR: sweepR, glow: 1.6, flare: 0.6 },
    { f: 5, hip: [c + 4, 0.03 * L], torso: lean - 0.12, head: 0.12, F: { x: -0.05 * L, lift: 0.16 * L, a: 0.45 }, R: { x: -(trail - 0.06) * L, lift: 0.3 * L, a: 1.0 }, armF: tuckF, armR: sweepR, glow: 1.4, flare: 0.4 },
    // 6-7 recovery
    { f: 6, hip: [c + 1, 0.07 * L], torso: s.lean + 0.25, head: 0.04, F: { x: fF, lift: 0.02 * L, a: 0.05 }, R: { x: fR - 0.12 * L, lift: 0.12 * L, a: 0.6 }, armF: [1.6, 0.6], armR: [1.9, 1.6], glow: 1.2, flare: 0.15 },
    { f: 7, hip: [c, 0.05 * L], torso: s.lean + 0.1, head: -0.02, F: { x: fF }, R: { x: fR, a: 0.15 }, armF: CARRY_F, armR: CARRY_R, glow: 1.05 },
  ];
}

function hurtKeys(s: MechStyle): Key[] {
  const L = legLen(s);
  const k = PERSONA[s.id].flinch;
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  return [
    { f: 0, hip: [c - 2 * k, 0.03 * L], torso: s.lean - 0.17 * k, head: -0.3 * k, F: { x: fF }, R: { x: fR }, armF: [1.85, 1.3], armR: [2.2, 1.85], glow: 1.7, ease: 'o' },
    { f: 1, hip: [c - 3 * k, 0.07 * L], torso: s.lean - 0.25 * k, head: -0.4 * k, F: { x: fF }, R: { x: fR, a: 0.18 }, armF: [2.05, 1.55], armR: [2.4, 2.1], glow: 1.25 },
    { f: 2, hip: [c - 1.5 * k, 0.05 * L], torso: s.lean - 0.11 * k, head: -0.16 * k, F: { x: fF }, R: { x: fR }, armF: [1.55, 0.85], armR: [1.6, 1.2], glow: 1.05 },
    { f: 3, hip: [c - 0.4 * k, 0.015 * L], torso: s.lean - 0.03 * k, head: -0.06, F: { x: fF }, R: { x: fR }, armF: [1.25, 0.4], armR: [1.1, 0.4], glow: 1 },
  ];
}

// ───────────────────────────── Land (absorb → recover) ─────────────────────────────

/**
 * Landing: the knees take the impact (hips drop ~¼ leg, the chest pitches forward and the
 * head dips), hold the compression for a beat, then rise smoothly back into the idle
 * stance. The game plays it phase-driven, scaled by the landing speed.
 */
function landKeys(s: MechStyle): Key[] {
  const L = legLen(s);
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s) + 0.4;
  return [
    { f: 0, hip: [c + 1, 0.17 * L], torso: s.lean + 0.26, head: 0.08, F: { x: fF + 1 }, R: { x: fR - 1, a: 0.12 }, armF: CARRY_F, armR: CARRY_R, glow: 1.2 },
    { f: 1, hip: [c + 1.6, 0.24 * L], torso: s.lean + 0.36, head: 0.12, F: { x: fF + 1 }, R: { x: fR - 1, a: 0.16 }, armF: CARRY_F, armR: CARRY_R, glow: 1.1, ease: 'o' },
    { f: 2, hip: [c + 1.2, 0.2 * L], torso: s.lean + 0.3, head: 0.08, F: { x: fF + 0.6 }, R: { x: fR - 0.6, a: 0.1 }, armF: CARRY_F, armR: CARRY_R, ease: 'io' },
    { f: 3, hip: [c + 0.6, 0.1 * L], torso: s.lean + 0.15, head: 0.02, F: { x: fF + 0.3 }, R: { x: fR - 0.3, a: 0.04 }, armF: CARRY_F, armR: CARRY_R, ease: 'io' },
    { f: 4, hip: [c + 0.2, 0.025 * L], torso: s.lean + 0.04, head: -0.03, F: { x: fF }, R: { x: fR }, armF: CARRY_F, armR: CARRY_R, ease: 'io' },
    { f: 5, hip: [c, 0], torso: s.lean, head: -0.04, F: { x: fF }, R: { x: fR }, armF: CARRY_F, armR: CARRY_R, ease: 'io' },
  ];
}

// ───────────────────────────── Death (shutdown → kneel → collapse) ─────────────────────────────

function deathKeys(s: MechStyle): Key[] {
  const L = legLen(s);
  const P = PERSONA[s.id];
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  const limpF: Pt = [1.9, 1.75];
  const limpR: Pt = [1.75, 1.6];
  const proneArmF: Pt = [0.55, 0.12];
  const proneArmR: Pt = [0.75, 0.3];
  return [
    // 0-3 hit, shutdown sparks, reactor flickering, head drops
    { f: 0, hip: [c - 2, 1], torso: s.lean - 0.22, head: -0.36, F: { x: fF }, R: { x: fR }, armF: [2.45, 2.05], armR: [2.65, 2.3], glow: 1.6, sparks: 1, dock: 0, ease: 'o' },
    { f: 1, hip: [c - 3, 2], torso: s.lean - 0.3, head: -0.44, F: { x: fF }, R: { x: fR, a: 0.15 }, armF: [2.3, 2.0], armR: [2.5, 2.2], glow: 0.55, sparks: 0.85 },
    { f: 2, hip: [c - 2.5, 3], torso: s.lean - 0.12, head: 0.32, F: { x: fF }, R: { x: fR }, armF: limpF, armR: limpR, glow: 1.0, sparks: 0.7, dock: 0.3 },
    { f: 3, hip: [c - 1, 0.13 * L], torso: s.lean + 0.08, head: 0.55, F: { x: fF - 1 }, R: { x: fR + 1, a: 0.25 }, armF: limpF, armR: limpR, glow: 0.35, sparks: 0.55, dock: 0.7, ease: 'i' },
    // 4-6 knees buckle into a kneel
    { f: 4, hip: [c, P.kneelDrop * 0.6], torso: s.lean + 0.25, head: 0.72, F: { x: P.kneelAnkle + 4, a: 0.6 }, R: { x: P.kneelAnkle - 2, a: 0.8 }, armF: [1.7, 1.6], armR: [1.6, 1.5], glow: 0.6, sparks: 0.45, dock: 1, ease: 'i' },
    { f: 5, hip: [c, P.kneelDrop], torso: s.lean + 0.32, head: 0.85, F: { x: P.kneelAnkle + 1, a: 1.1 }, R: { x: P.kneelAnkle - 3, a: 1.15 }, armF: [1.55, 1.5], armR: [1.5, 1.45], glow: 0.22, sparks: 0.35, dock: 1, ease: 'o' },
    { f: 6, hip: [c, P.kneelDrop + 0.5], torso: s.lean + 0.5, head: 0.95, F: { x: P.kneelAnkle, a: 1.15 }, R: { x: P.kneelAnkle - 4, a: 1.2 }, armF: [1.45, 1.4], armR: [1.45, 1.4], glow: 0.3, sparks: 0.25, dock: 1 },
    // 7-9 topple forward onto the ground
    { f: 7, hip: [c + 1.5, P.kneelDrop - 1], torso: 0.92, head: 1.15, F: { x: P.kneelAnkle - 4, a: 1.25 }, R: { x: P.kneelAnkle - 8, a: 1.3 }, armF: [1.05, 1.0], armR: [1.15, 1.1], glow: 0.12, sparks: 0.15, dock: 1, ease: 'i' },
    { f: 8, hip: [P.proneHipX + 2, (P.kneelDrop + P.proneDrop) / 2 + 2], torso: 1.24, head: 1.38, F: { x: (P.kneelAnkle + P.proneLegX) / 2, lift: 2, a: 1.38 }, R: { x: (P.kneelAnkle + P.proneLegX) / 2 - 4, lift: 2, a: 1.42 }, armF: [0.8, 0.55], armR: [0.95, 0.7], glow: 0.08, sparks: 0.1, dock: 1, ease: 'i' },
    { f: 9, hip: [P.proneHipX, P.proneDrop], torso: 1.52, head: 1.5, F: { x: P.proneLegX + 4, lift: P.proneLift, a: 1.45 }, R: { x: P.proneLegX, lift: P.proneLift, a: 1.5 }, armF: proneArmF, armR: proneArmR, glow: 0.12, sparks: 0.2, dock: 1, ease: 'i' },
    // 10-11 impact bounce, settle; reactor dies
    { f: 10, hip: [P.proneHipX, P.proneDrop - 1.2], torso: 1.49, head: 1.42, F: { x: P.proneLegX + 4, lift: P.proneLift + 1.5, a: 1.42 }, R: { x: P.proneLegX, lift: P.proneLift + 1.5, a: 1.47 }, armF: proneArmF, armR: proneArmR, glow: 0.04, sparks: 0.05, dock: 1, ease: 'o' },
    { f: 11, hip: [P.proneHipX, P.proneDrop], torso: 1.53, head: 1.52, F: { x: P.proneLegX + 4, lift: P.proneLift, a: 1.45 }, R: { x: P.proneLegX, lift: P.proneLift, a: 1.5 }, armF: proneArmF, armR: proneArmR, glow: 0, sparks: 0, dock: 1 },
  ];
}

// ───────────────────────────── Signatures ─────────────────────────────

/** VOLT — Arc Dash: coil, explosive lunge with the weapon arm thrust forward, sliding recovery. */
function voltSignature(s: MechStyle): Key[] {
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  const thrustF: Pt = [0.22, 0.04];
  const trailR: Pt = [2.7, 2.95];
  return [
    { f: 0, hip: [c, 4], torso: s.lean + 0.2, head: 0.02, F: { x: fF + 2 }, R: { x: fR - 2 }, armF: [1.9, 1.15], armR: [2.1, 1.6], glow: 1.3, arcs: 0.3, flare: 0.3 },
    { f: 2, hip: [c - 2, 10], torso: 0.52, head: 0.1, F: { x: fF + 4 }, R: { x: fR - 5, a: 0.32 }, armF: [2.3, 1.65], armR: [2.5, 2.0], glow: 1.65, arcs: 0.7, flare: 0.7, ease: 'o' },
    { f: 3, hip: [c + 7, 9], torso: 0.86, head: 0.26, F: { x: 21, lift: 4, a: 0.08 }, R: { x: -26, a: 0.66 }, armF: thrustF, armR: trailR, glow: 1.9, arcs: 1, flare: 1, ease: 'o' },
    { f: 4, hip: [c + 10, 10], torso: 0.95, head: 0.3, F: { x: 24 }, R: { x: -30, a: 0.9 }, armF: thrustF, armR: trailR, glow: 1.9, arcs: 1, flare: 1 },
    { f: 5, hip: [c + 11, 10.5], torso: 0.96, head: 0.3, F: { x: 25 }, R: { x: -31, a: 0.92 }, armF: thrustF, armR: trailR, glow: 1.85, arcs: 0.95, flare: 1 },
    { f: 7, hip: [c + 9, 9], torso: 0.86, head: 0.26, F: { x: 24 }, R: { x: -28, a: 0.85 }, armF: [0.35, 0.1], armR: [2.5, 2.7], glow: 1.6, arcs: 0.75, flare: 0.85 },
    { f: 8, hip: [c + 6, 7], torso: 0.6, head: 0.14, F: { x: 19 }, R: { x: -20, lift: 3, a: 0.6 }, armF: [0.8, 0.35], armR: [1.9, 1.5], glow: 1.4, arcs: 0.45, flare: 0.6 },
    { f: 9, hip: [c + 3, 4.5], torso: 0.38, head: 0.04, F: { x: 15 }, R: { x: -14, a: 0.2 }, armF: [1.0, 0.3], armR: [1.2, 0.6], glow: 1.2, arcs: 0.2, flare: 0.3 },
    { f: 11, hip: [c, 1], torso: s.lean + 0.04, head: -0.03, F: { x: fF + 1 }, R: { x: fR }, armF: CARRY_F, armR: CARRY_R, glow: 1.05, arcs: 0, flare: 0 },
  ];
}

/** BASTION — Siege Field: rear up, stomp and plant with heel anchors, braced hold. */
function bastionSignature(s: MechStyle): Key[] {
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  return [
    { f: 0, hip: [c, 1], torso: s.lean, head: -0.04, F: { x: fF }, R: { x: fR }, armF: [1.0, 0.4], armR: [1.0, 0.5], glow: 1.1 },
    { f: 2, hip: [c - 2, -2], torso: -0.07, head: -0.12, F: { x: fF + 4, lift: 9, a: -0.15 }, R: { x: fR }, armF: [-0.25, -0.85], armR: [0.35, -0.3], glow: 1.3, flare: 0.3, ease: 'o' },
    { f: 3, hip: [c - 1.5, -2.5], torso: -0.09, head: -0.14, F: { x: fF + 6, lift: 11, a: -0.2 }, R: { x: fR }, armF: [-0.45, -1.05], armR: [0.2, -0.45], glow: 1.4, flare: 0.4 },
    { f: 4, hip: [c + 1, 8], torso: 0.1, head: 0.06, F: { x: fF + 7 }, R: { x: fR - 3 }, armF: [1.05, 1.35], armR: [1.1, 1.4], glow: 1.8, flare: 1, anchor: 0.65, ease: 'i' },
    { f: 5, hip: [c + 1, 10.5], torso: 0.12, head: 0.08, F: { x: fF + 7 }, R: { x: fR - 3 }, armF: [1.1, 1.4], armR: [1.15, 1.45], glow: 1.9, flare: 1, anchor: 1, ease: 'o' },
    { f: 6, hip: [c + 1, 9], torso: 0.08, head: 0.04, F: { x: fF + 7 }, R: { x: fR - 3 }, armF: [0.85, 0.75], armR: [0.95, 0.85], glow: 1.7, flare: 1, anchor: 1 },
    { f: 8, hip: [c + 1, 9.5], torso: 0.06, head: 0.02, F: { x: fF + 7 }, R: { x: fR - 3 }, armF: [0.55, 0.15], armR: [0.65, 0.25], glow: 1.55, flare: 0.85, anchor: 1 },
    { f: 9, hip: [c + 1, 9], torso: 0.06, head: 0.0, F: { x: fF + 7 }, R: { x: fR - 3 }, armF: [0.55, 0.15], armR: [0.65, 0.25], glow: 1.5, flare: 0.8, anchor: 1 },
    { f: 10, hip: [c + 0.5, 6], torso: 0.05, head: -0.02, F: { x: fF + 5 }, R: { x: fR - 2 }, armF: [0.8, 0.25], armR: [0.8, 0.3], glow: 1.3, flare: 0.4, anchor: 0.55 },
    { f: 11, hip: [c, 3], torso: s.lean + 0.02, head: -0.04, F: { x: fF + 4 }, R: { x: fR - 2 }, armF: CARRY_F, armR: CARRY_R, glow: 1.1, flare: 0.1, anchor: 0.15 },
  ];
}

/** WRAITH — Phase Echo: fold into a low phasing crouch while the floating parts flare outward. */
function wraithSignature(s: MechStyle): Key[] {
  const fF = s.stanceF;
  const fR = s.stanceR;
  const c = midX(s);
  return [
    { f: 0, hip: [c, 3], torso: s.lean + 0.08, head: -0.02, F: { x: fF }, R: { x: fR }, armF: [1.4, 0.9], armR: [1.2, 0.8], glow: 1.2, flare: 0.2, bob: 0 },
    { f: 2, hip: [c - 1, 12], torso: 0.3, head: 0.06, F: { x: fF + 5 }, R: { x: fR - 6, a: 0.5 }, armF: [2.0, 0.6], armR: [2.2, 0.9], glow: 1.5, flare: 0.6, bob: -1, ease: 'o' },
    { f: 3, hip: [c - 1, 17], torso: 0.38, head: 0.1, F: { x: fF + 7 }, R: { x: fR - 8, a: 0.9 }, armF: [0.9, 0.2], armR: [2.6, 3.0], glow: 1.8, flare: 0.95, bob: -1.5 },
    { f: 5, hip: [c - 1, 18], torso: 0.4, head: 0.12, F: { x: fF + 7 }, R: { x: fR - 8, a: 0.92 }, armF: [0.8, 0.12], armR: [2.7, 3.1], glow: 1.95, flare: 1, bob: 1.5 },
    { f: 7, hip: [c - 1, 17.5], torso: 0.4, head: 0.11, F: { x: fF + 7 }, R: { x: fR - 8, a: 0.92 }, armF: [0.85, 0.15], armR: [2.65, 3.05], glow: 1.9, flare: 1, bob: -1.2 },
    { f: 8, hip: [c - 1, 12], torso: 0.28, head: 0.06, F: { x: fF + 5 }, R: { x: fR - 6, a: 0.55 }, armF: [1.1, 0.4], armR: [2.0, 1.6], glow: 1.5, flare: 0.7, bob: 0 },
    { f: 9, hip: [c, 7], torso: 0.18, head: 0.0, F: { x: fF + 3 }, R: { x: fR - 3, a: 0.2 }, armF: [1.1, 0.35], armR: [1.3, 0.7], glow: 1.3, flare: 0.4, bob: 0.6 },
    { f: 11, hip: [c, 1], torso: s.lean, head: -0.04, F: { x: fF }, R: { x: fR }, armF: CARRY_F, armR: CARRY_R, glow: 1.05, flare: 0, bob: 0 },
  ];
}

const SIGNATURES: Record<MechId, (s: MechStyle) => Key[]> = {
  volt: voltSignature,
  bastion: bastionSignature,
  wraith: wraithSignature,
};

/** Aim weight ramps back in over the last recovery frames so blending out of an action never snaps. */
const rampIn = (count: number, from: number): ((i: number) => number) => (i) =>
  i < from ? 0 : Math.min(0.75, ((i - from + 1) / (count - from + 1)) * 0.9);

/** Every frame of every action for one mech, in ANIM_KEYS order. */
export function buildAnimFrames(style: MechStyle): Record<AnimKey, FrameSpec[]> {
  return {
    idle: idleFrames(style),
    run: runFrames(style),
    jump: sampleKeys(style, 'jump', jumpKeys(style), 8, () => 1),
    dash: sampleKeys(style, 'dash', dashKeys(style), 8, rampIn(8, 6)),
    shoot: shootFrames(style),
    signature: sampleKeys(style, 'signature', SIGNATURES[style.id](style), 12, rampIn(12, 10)),
    hurt: sampleKeys(style, 'hurt', hurtKeys(style), 4, () => 0.5),
    land: sampleKeys(style, 'land', landKeys(style), 6, () => 1),
    death: sampleKeys(style, 'death', deathKeys(style), 12, () => 0),
  };
}
